import React, {useRef, useState} from 'react'
import NLConfigurableBlock, {
  ConfigurableBlockTargetProps,
} from 'organization/Marketplace/config/NLConfigurableBlock'
import {NLPurchasePageBlock} from 'Event/Marketplace/nl-purchase-page'
import {HashMap, orderedIdsByPosition} from 'lib/list'
import {BlockComponentProps} from 'Event/Marketplace/Block'
import ButtonConfig from 'organization/Marketplace/config/BlockConfig/ButtonConfig'
import AddBlockButton from 'organization/Marketplace/config/AddBlockButton'
import {ColumnBox, ColumnProps} from 'Event/Marketplace/Block/RowBlock/Column'
import NLAddBlockDialog from 'organization/Marketplace/config/NLAddBlockDialog'
import styled from 'styled-components'
import {useDrag} from 'organization/Marketplace/config/DragContext'

type ConfigurableColumnProps = ColumnProps &
  BlockComponentProps &
  ConfigurableBlockTargetProps

export const movedBlockEvent = 'MOVED_BLOCK_EVENT'

type MovedBlockOntoBlockEventDetail = {
  sourceBlockPath: string
  targetColumnPath: string
  targetBlockId: string
  targetBlockOffset: 'above' | 'below'
  targetColumnNumber: number
}

type MovedBlockOntoEmptyColumnEventDetail = {
  sourceBlockPath: string
  targetColumnPath: string
  targetBlockId: null
  targetBlockOffset: null
  targetColumnNumber: number
}

export type MovedBlockEventDetail =
  | MovedBlockOntoBlockEventDetail
  | MovedBlockOntoEmptyColumnEventDetail

// TODO:
// - show different border color for row/column

export default function NLConfigurableColumn(props: ConfigurableColumnProps) {
  const {
    blocks,
    number,
    template,
    getPreviewData,
    availableBlocks,
    blockPath,
    onSave,
  } = props

  const columnRef = useRef<HTMLDivElement | null>(null)

  const [insertTargetBlockId, setInsertTargetBlockId] = useState<string | null>(
    null,
  )
  const [dragOverBlockId, setDragOverBlockId] = useState<string | null>(null)
  const [dragPosition, setDragPosition] = useState<'above' | 'below' | null>(
    null,
  )

  const childBlocks: HashMap<NLPurchasePageBlock> = {}
  for (const [blockId, block] of Object.entries(blocks)) {
    if (block.column === number) {
      childBlocks[blockId] = block
    }
  }

  const blockIds = orderedIdsByPosition(childBlocks)

  const isEmpty = Object.keys(childBlocks).length === 0

  const {
    draggedBlockPath,
    isDragging,
    setDraggedBlock,
    setIsDragging,
    resetDrag,
  } = useDrag()

  const handleDragStart = (params: {
    dragEvent: React.DragEvent
    id: string
    block: NLPurchasePageBlock
  }) => {
    const {dragEvent, id} = params

    setIsDragging(true)

    // hide overlay to show content only being dragged
    const overlayItems = (document.querySelectorAll(
      '.block-overlay-item',
    ) as unknown) as HTMLElement[]

    for (const item of overlayItems) {
      item.style.opacity = '0'
    }

    const draggedBlockPath = `${blockPath}.blocks.${id}`
    setDraggedBlock(draggedBlockPath)

    const blockElement = document.getElementById(`block-${id}`)
    if (blockElement) {
      dragEvent.dataTransfer.setDragImage(blockElement, 10, 10)
    }

    dragEvent.dataTransfer.setData(
      'text/plain',
      JSON.stringify({
        blockPath: draggedBlockPath,
      }),
    )
    dragEvent.dataTransfer.effectAllowed = 'move'
  }

  const handleDragOver = (params: {
    dragEvent: React.DragEvent
    id: string
    block: NLPurchasePageBlock
  }) => {
    const {dragEvent, id} = params
    dragEvent.preventDefault()
    dragEvent.stopPropagation()

    if (!isDragging || !draggedBlockPath) {
      return
    }

    const draggedBlockId = draggedBlockPath.split('.').pop()
    const isDraggedBlock = draggedBlockId === id
    if (isDraggedBlock) {
      return
    }

    // Calculate if we're in the top or bottom half of the target block
    const targetRect = dragEvent.currentTarget.getBoundingClientRect()
    const targetMiddleY = targetRect.top + targetRect.height / 2
    const position = dragEvent.clientY < targetMiddleY ? 'above' : 'below'

    setDragOverBlockId(id)
    setDragPosition(position)
  }

  const handleDragLeave = () => {
    setDragOverBlockId(null)
    setDragPosition(null)
  }

  const handleDragEnd = () => {
    setDragOverBlockId(null)
    setDragPosition(null)
    resetDrag()

    // Restore overlay items visibility
    const overlayItems = (document.querySelectorAll(
      '.block-overlay-item',
    ) as unknown) as HTMLElement[]

    for (const item of overlayItems) {
      item.style.opacity = '1'
    }
  }

  const handleDropOnBlock = (params: {
    dragEvent: React.DragEvent
    id: string
    block: NLPurchasePageBlock
  }) => {
    const {dragEvent, id} = params

    dragEvent.preventDefault()
    dragEvent.stopPropagation()

    if (!draggedBlockPath || !dragPosition) {
      handleDragEnd()
      return
    }

    const detail: MovedBlockEventDetail = {
      sourceBlockPath: draggedBlockPath,
      targetColumnPath: blockPath,
      targetBlockId: id,
      targetBlockOffset: dragPosition,
      targetColumnNumber: number,
    }

    const event = new CustomEvent(movedBlockEvent, {
      detail,
      bubbles: true, // so that the event can be caught by the parent
    })

    if (columnRef.current) {
      columnRef.current.dispatchEvent(event)
    }

    handleDragEnd()
  }

  const handleDropOnColumn = (params: {dragEvent: React.DragEvent}) => {
    const {dragEvent} = params

    dragEvent.stopPropagation()
    dragEvent.preventDefault()

    if (!draggedBlockPath) {
      handleDragEnd()
      return
    }

    const detail: MovedBlockOntoEmptyColumnEventDetail = {
      sourceBlockPath: draggedBlockPath,
      targetColumnPath: blockPath,
      targetColumnNumber: number,
      targetBlockId: null,
      targetBlockOffset: null,
    }

    const event = new CustomEvent(movedBlockEvent, {
      detail,
      bubbles: true, // so that the event can be caught by the parent
    })

    if (columnRef.current) {
      columnRef.current.dispatchEvent(event)
    }

    handleDragEnd()
  }

  return (
    <>
      <NLAddBlockDialog
        open={Boolean(insertTargetBlockId)}
        onClose={() => setInsertTargetBlockId(null)}
        availableBlocks={availableBlocks}
        onAdd={onSave}
        column={number}
        insertTargetBlockId={insertTargetBlockId}
        blockIds={blockIds}
      />
      <ColumnBox ref={columnRef}>
        <DraggableBlocksContainer
          onDragOver={(e) => {
            e.preventDefault()
            if (isEmpty) {
              e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)'
            }
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            if (isEmpty) {
              e.currentTarget.style.background = 'transparent'
            }
          }}
          onDrop={(e) => {
            e.preventDefault()
            if (isEmpty) {
              e.currentTarget.style.background = 'transparent'
            }

            handleDropOnColumn({
              dragEvent: e,
            })
          }}
        >
          {blockIds.map((id) => (
            <DraggableBlock
              key={id}
              id={`block-${id}`}
              isOver={dragOverBlockId === id}
              isAbove={dragOverBlockId === id && dragPosition === 'above'}
              isBelow={dragOverBlockId === id && dragPosition === 'below'}
              isDragging={draggedBlockPath?.endsWith(id) || false}
              onDragOver={(dragEvent) =>
                handleDragOver({
                  dragEvent,
                  id,
                  block: childBlocks[id],
                })
              }
              onDragLeave={handleDragLeave}
              onDrop={(dragEvent) =>
                handleDropOnBlock({
                  dragEvent,
                  id,
                  block: childBlocks[id],
                })
              }
            >
              <NLConfigurableBlock
                id={id}
                block={childBlocks[id]}
                template={template}
                getPreviewData={(data) =>
                  getPreviewData({
                    blocks: {
                      [id]: data,
                    },
                  })
                }
                availableBlocks={availableBlocks.filter(
                  (type) => type !== 'Row',
                )}
                ButtonConfig={ButtonConfig}
                blockPath={blockPath}
                onClickAddBlock={() => {
                  setInsertTargetBlockId(id)
                }}
                onSave={(data) => {
                  onSave({
                    blocks: {
                      [id]: data,
                    },
                  })
                }}
                onDragStart={(dragEvent) =>
                  handleDragStart({
                    dragEvent,
                    id,
                    block: childBlocks[id],
                  })
                }
              />
            </DraggableBlock>
          ))}
        </DraggableBlocksContainer>
        {isEmpty && (
          <AddBlockButton
            onClick={(e) => {
              e.stopPropagation()
              setInsertTargetBlockId('end')
            }}
          />
        )}
      </ColumnBox>
    </>
  )
}

const DraggableBlocksContainer = styled.div`
  width: 100%;
  min-height: 20px;
  transition: background 0.2s ease;
`

const DraggableBlock = styled.div<{
  isDragging: boolean
  isOver: boolean
  isAbove: boolean
  isBelow: boolean
}>`
  position: relative;
  opacity: ${(props) => (props.isDragging ? 0.5 : 1)};
  margin-bottom: 10px;
  transition: transform 0.2s ease;

  /* Top insert indicator */
  &:before {
    content: '';
    position: absolute;
    top: -5px;
    left: 0;
    right: 0;
    height: ${(props) => (props.isAbove ? '4px' : '0')};
    background-color: ${(props) => props.theme.colors.primary};
    transition: all 0.2s ease;
    z-index: 10000;
  }

  /* Bottom insert indicator */
  &:after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    right: 0;
    height: ${(props) => (props.isBelow ? '4px' : '0')};
    background-color: ${(props) => props.theme.colors.primary};
    transition: all 0.2s ease;
    z-index: 10000;
  }
`
